import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Import all necessary Firebase functions and your project's config
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { db, firebaseConfig } from '../firebase'; // Import the main db instance and the config object
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';

const AddEditUserPage = () => {
    // State for the form fields
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [selectedRole, setSelectedRole] = useState('');
    
    // State to hold the list of available roles fetched from Firestore
    const [availableRoles, setAvailableRoles] = useState([]);
    
    // State for loading indicators and error messages
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const navigate = useNavigate();

    // This hook runs once when the component mounts to fetch all available roles
    // and populate the dropdown menu.
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const rolesCollectionRef = collection(db, 'roles');
                const querySnapshot = await getDocs(rolesCollectionRef);
                
                const rolesList = querySnapshot.docs.map(doc => ({
                    id: doc.id, // e.g., "Admin", "InventoryManager"
                    ...doc.data()
                }));
                
                setAvailableRoles(rolesList);

                // Automatically select the first role in the list as the default
                if (rolesList.length > 0) {
                    setSelectedRole(rolesList[0].id);
                }
            } catch (err) {
                console.error("Failed to fetch roles:", err);
                setError("Could not load roles from the database.");
            }
        };

        fetchRoles();
    }, []); // The empty dependency array [] ensures this runs only once on mount

    // This function handles the form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedRole) {
            setError('Please select a role for the new user.');
            return;
        }

        setIsLoading(true);
        setError('');

        let secondaryApp; // This variable will hold our temporary Firebase app instance

        try {
            // Step 1: Initialize a temporary, secondary Firebase app.
            // We give it a unique name using the current timestamp to avoid conflicts.
            secondaryApp = initializeApp(firebaseConfig, `SecondaryApp_${Date.now()}`);
            const secondaryAuth = getAuth(secondaryApp);

            // Step 2: Create the user in Firebase Authentication using the temporary app.
            // This creates the user but does NOT affect the main app's authentication state.
            const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
            const newUser = userCredential.user;

            // Step 3: Create the user's profile document in Firestore using the main app's
            // database connection, which is authenticated as the logged-in Admin.
            const userDocRef = doc(db, 'users', newUser.uid);
            await setDoc(userDocRef, {
                email: newUser.email,
                roles: [selectedRole] // The roles field is an array with the selected role
            });

            alert('User created successfully!');
            navigate('/users'); // Redirect the Admin to the user list page after success

        } catch (err) {
            console.error("Error creating user:", err);
            // Provide more user-friendly error messages for common issues
            if (err.code === 'auth/email-already-in-use') {
                setError('This email address is already in use by another account.');
            } else if (err.code === 'auth/weak-password') {
                setError('The password must be at least 6 characters long.');
            } else {
                // This will catch other errors, including permission errors from security rules
                setError(err.message);
            }
        } finally {
            // Step 4: CRITICAL - Always clean up and delete the temporary app instance
            // to prevent memory leaks and other issues.
            if (secondaryApp) {
                await deleteApp(secondaryApp);
            }
            setIsLoading(false);
        }
    };

    // JSX for rendering the form
    return (
        <div style={styles.container}>
            <h2 style={styles.title}>Create New User</h2>
            {error && <p style={styles.error}>{error}</p>}
            <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.formGroup}>
                    <label style={styles.label}>Email Address</label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={styles.input}
                        required
                    />
                </div>
                <div style={styles.formGroup}>
                    <label style={styles.label}>Password</label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={styles.input}
                        required
                    />
                </div>
                <div style={styles.formGroup}>
                    <label style={styles.label}>Role</label>
                    <select
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        style={styles.input}
                        required
                    >
                        {availableRoles.length === 0 ? (
                            <option disabled>Loading roles...</option>
                        ) : (
                            availableRoles.map(role => (
                                <option key={role.id} value={role.id}>
                                    {role.id}
                                </option>
                            ))
                        )}
                    </select>
                </div>
                <button type="submit" style={styles.button} disabled={isLoading}>
                    {isLoading ? 'Creating User...' : 'Create User'}
                </button>
            </form>
        </div>
    );
};

// Basic styling for the component
const styles = {
    container: {
        padding: '2rem',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    },
    title: {
        marginBottom: '1.5rem',
        color: '#333'
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem'
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem'
    },
    label: {
        fontWeight: 'bold',
        color: '#555'
    },
    input: {
        padding: '0.8rem',
        borderRadius: '4px',
        border: '1px solid #ddd',
        fontSize: '1rem'
    },
    button: {
        padding: '0.8rem',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '1rem',
        marginTop: '1rem'
    },
    error: {
        color: '#dc3545',
        backgroundColor: '#f8d7da',
        padding: '0.75rem',
        borderRadius: '4px',
        marginBottom: '1rem',
        border: '1px solid #f5c6cb'
    }
};

export default AddEditUserPage;